<div class="sort-scans-container">
  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filter-row">
      <!-- Date Range Picker (max 7 days, no future dates) -->
      <mat-form-field appearance="outline" class="date-range-field">
        <mat-label>Date range</mat-label>
        <mat-date-range-input [formGroup]="range" [rangePicker]="picker" [max]="maxDate">
          <input matStartDate formControlName="start" placeholder="Start date" (dateChange)="onStartDateChange($event)" />
          <input matEndDate formControlName="end" placeholder="End date" />
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker (opened)="onPickerOpened()"></mat-date-range-picker>
      </mat-form-field>

      <!-- Location Filter -->
      <mat-form-field appearance="outline" class="location-filter">
        <mat-label>Airport: {{ selectedLocations.length === 0 ? 'All' : '' }}</mat-label>
        <mat-select [(value)]="selectedLocations" multiple>
          <mat-option *ngFor="let locationCode of locationOptions" [value]="locationCode">
            {{ locationCode }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      
      <!-- Search Button -->
      <button mat-flat-button class='align-right' color='primary' type='submit' (click)='loadData()'>Search</button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class='align-center mt--lg'>
    <app-loading></app-loading>
  </div>

  <!-- Error State -->
  <div *ngIf="hasError && !loading" class="error-container">
    <mat-icon class="error-icon">error_outline</mat-icon>
    <span class="error-text">{{ errorMessage }}</span>
    <button mat-stroked-button color="primary" (click)="loadData()">Retry</button>
  </div>

  <!-- Data Display -->
  <div *ngIf="!loading && !hasError && responseData" class="data-section">
    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card">
        <div class="card-icon blue">
          <mat-icon class='material-symbols-outlined'>document_scanner</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ formatNumber(responseData.totalFirstMileScans) }}</div>
          <div class="card-label">First Mile Scans</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon green">
          <mat-icon class='material-symbols-outlined'>barcode_scanner</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ formatNumber(responseData.totalLastMileScans) }}</div>
          <div class="card-label">Last Mile Scans</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="card-icon yellow">
          <mat-icon class='material-symbols-outlined'>barcode_reader</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ formatNumber(responseData.totalScans) }}</div>
          <div class="card-label">Total Scanned</div>
        </div>
      </div>
      <div *ngIf="!isAppleExpressUser" class="summary-card">
        <div class="card-icon purple">
          <mat-icon class='material-symbols-outlined'>request_quote</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">${{ formatNumber(responseData.totalCost) }}</div>
          <div class="card-label">Total Cost</div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-container" *ngIf="responseData.data.length > 0">
      <table mat-table [dataSource]="responseData.data" class="sort-scans-table">
        <!-- Location Column -->
        <ng-container matColumnDef="location">
          <th mat-header-cell *matHeaderCellDef class="location-header">Airport / Date</th>
          <td mat-cell *matCellDef="let location">
            <div class="location-cell">
              <span class="location-code">{{ location.location }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Dynamic Date Columns -->
        <ng-container *ngFor="let dateCol of dateColumns; let i = index" [matColumnDef]="'date_' + i">
          <th mat-header-cell *matHeaderCellDef class="date-header">
            <div class="date-header-content">
              <span class="date-main">{{ getFormattedDateHeader(i) }}</span>
              <div class="date-sub-headers">
                <span class="sub-header">Out</span>
                <span class="sub-header">In</span>
              </div>
            </div>
          </th>
          <td mat-cell *matCellDef="let location" class="date-cell">
            <div class="cell-values">
              <span class="outbound-value">{{ getOutboundCount(location, i) }}</span>
              <span class="inbound-value">{{ getInboundCount(location, i) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Total Scanned Column (per location across all dates) -->
        <ng-container matColumnDef="total_scanned">
          <th mat-header-cell *matHeaderCellDef class="date-header total-header">
            <div class="date-header-content">
              <span class="date-main">Total Scanned</span>
              <div class="date-sub-headers">
                <span class="sub-header">Out</span>
                <span class="sub-header">In</span>
              </div>
            </div>
          </th>
          <td mat-cell *matCellDef="let location" class="date-cell total-cell">
            <div class="cell-values">
              <span class="outbound-value">{{ formatNumber(location.totalOutboundSortingScansForLocation) }}</span>
              <span class="inbound-value">{{ formatNumber(location.totalInboundSortingScansForLocation) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Footer: Location Column -->
        <ng-container matColumnDef="location-footer">
          <td mat-footer-cell *matFooterCellDef class="footer-label">DAILY TOTAL</td>
        </ng-container>

        <!-- Footer: Dynamic Date Columns -->
        <ng-container *ngFor="let dateCol of dateColumns; let i = index" [matColumnDef]="'date_' + i + '_footer'">
          <td mat-footer-cell *matFooterCellDef class="footer-cell">
            <div class="cell-values">
              <span class="outbound-value">{{ formatNumber(getDailyTotalOutbound(i)) }}</span>
              <span class="inbound-value">{{ formatNumber(getDailyTotalInbound(i)) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Footer: Total Scanned Column -->
        <ng-container matColumnDef="total_scanned_footer">
          <td mat-footer-cell *matFooterCellDef class="footer-cell total-cell">
            <div class="cell-values">
              <span class="outbound-value">{{ formatNumber(getGrandTotalOutbound()) }}</span>
              <span class="inbound-value">{{ formatNumber(getGrandTotalInbound()) }}</span>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        <tr mat-footer-row *matFooterRowDef="footerColumns" class="footer-row"></tr>
      </table>
    </div>

    <!-- Empty State -->
    <div *ngIf="responseData.data.length === 0" class="empty-state">
      <mat-icon>inbox</mat-icon>
      <span>No sort scan data found for the selected filters.</span>
    </div>

    <!-- Footer: Export -->
    <div class="table-footer" *ngIf="responseData.data.length > 0 && canExportCsv">
      <button mat-flat-button color='primary' type='submit' [disabled]="exporting || !range.value.start || !range.value.end || responseData.data.length === 0" (click)='exportToCsv()'>
        <app-loading [inButton]='true' *ngIf='exporting'></app-loading>
        <span *ngIf='!exporting'>Export Data</span>
      </button>
    </div>
  </div>
</div>
